<template>
    <div class="dashboard-header">
        <div class="header-left">
            <div style="display: flex; gap: 10px">
                <h1 class="dashboard-title">요금 시뮬레이션</h1>
                <div
                    v-if="statusInfo"
                    class="status-message-container"
                    :class="`${statusInfo.status ? statusInfo.status.toLowerCase() : ''}`"
                >
                    <span class="status-marker"></span>
                    <span v-if="statusInfo.status" class="status-label">
                        {{ getStatusName(statusInfo.status) }}
                    </span>
                    <span class="status-separator">·</span>
                    <span v-if="statusInfo.message" class="status-message">
                        {{ statusInfo.message }}
                    </span>
                </div>
            </div>
        </div>

        <div class="header-right">
            <div class="kepco-customer-select-container">
                <select
                    v-model="localSelectedContractIdx"
                    class="kepco-customer-select"
                    :disabled="validContracts.length === 0"
                    @change="handleContractSelect"
                >
                    <option v-if="validContracts.length === 0" :value="null">계약 없음</option>
                    <option
                        v-for="contract in validContracts"
                        :key="contract.contractIdx"
                        :value="contract.contractIdx"
                    >
                        {{ getContractDisplayText(contract) }}
                    </option>
                </select>
                <i class="fas fa-chevron-down select-chevron" aria-hidden="true"></i>
            </div>

            <!-- 분석 유형 토글 -->
            <div class="analysis-type-toggle">
                <button
                    :class="['toggle-btn', { active: analysisType === 'monthly' }]"
                    @click="$emit('analysis-type-change', 'monthly')"
                >
                    월간
                </button>
                <button
                    :class="['toggle-btn', { active: analysisType === 'yearly' }]"
                    @click="$emit('analysis-type-change', 'yearly')"
                >
                    연간
                </button>
            </div>

            <span class="toolbar-divider" aria-hidden="true"></span>

            <!-- 기간 선택 -->
            <div class="date-picker-container" :class="{ loading: isLoading }" v-click-outside="handleCloseDatePicker">
                <button class="nav-btn" @click="$emit('date-navigate', -1)" :disabled="isLoading">
                    <i class="fas fa-chevron-left"></i>
                </button>
                <div class="date-display" @click="!isLoading && handleToggleDatePicker()">
                    <span>{{ currentDateDisplay }}</span>
                    <i v-if="isLoading" class="fas fa-spinner fa-spin loading-icon"></i>
                    <i v-else :class="['fas fa-chevron-down', { rotate: showDatePicker }]"></i>
                </div>
                <button class="nav-btn" @click="$emit('date-navigate', 1)" :disabled="isLoading">
                    <i class="fas fa-chevron-right"></i>
                </button>

                <!-- 드롭다운 -->
                <div v-if="showDatePicker" class="date-dropdown">
                    <div class="year-selector">
                        <button @click="$emit('year-change', -1)">
                            <i class="fas fa-chevron-left"></i>
                        </button>
                        <span>{{ currentYear }}</span>
                        <button @click="$emit('year-change', 1)">
                            <i class="fas fa-chevron-right"></i>
                        </button>
                    </div>
                    <div v-if="analysisType === 'monthly'" class="month-grid">
                        <button
                            v-for="month in 12"
                            :key="month"
                            :class="['month-btn', { active: month === currentMonth }]"
                            @click="$emit('month-select', month)"
                        >
                            {{ month }}월
                        </button>
                    </div>
                </div>
            </div>

            <button
                class="kepco-hourly-analysis-btn"
                :class="{ disabled: analysisType === 'yearly' }"
                :disabled="analysisType === 'yearly'"
                @click="$emit('usage-analysis-open')"
                :title="analysisType === 'yearly' ? '월간 기간에서만 사용 가능합니다' : '데이터 분석'"
            >
                <i class="fas fa-clock"></i>
                데이터 분석
            </button>
            <button
                class="kepco-pdf-btn"
                :disabled="!canExport || isExporting"
                title="현재 화면을 PDF 로 저장"
                @click="$emit('pdf-export')"
            >
                <i :class="isExporting ? 'fas fa-spinner fa-spin' : 'fas fa-file-pdf'"></i>
                PDF
            </button>

            <span class="toolbar-divider" aria-hidden="true"></span>

            <!-- 새로고침 버튼 -->
            <button v-b-tooltip.hover title="새로고침" class="btn-refresh" @click="$emit('refresh')">
                <i class="fas fa-sync-alt"></i>
            </button>

            <!-- 설정 버튼 -->
            <button v-b-tooltip.hover title="설정" class="btn-settings" @click="$emit('settings-open')">
                <i class="fas fa-cog"></i>
            </button>
        </div>
    </div>
</template>

<script>
import { getStatusName, getContractDisplayText } from "../../utils/helpers.js";

export default {
    name: "AnalysisHeader",
    directives: {
        clickOutside: {
            bind(el, binding) {
                el.clickOutsideEvent = function (event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.body.addEventListener("click", el.clickOutsideEvent);
            },
            unbind(el) {
                document.body.removeEventListener("click", el.clickOutsideEvent);
            },
        },
    },
    props: {
        statusInfo: {
            type: Object,
            default: null,
        },
        selectedContractIdx: {
            type: Number,
            default: null,
        },
        validContracts: {
            type: Array,
            default: () => [],
        },
        analysisType: {
            type: String,
            default: "monthly",
            validator: (value) => ["monthly", "yearly"].includes(value),
        },
        currentYear: {
            type: Number,
            required: true,
        },
        currentMonth: {
            type: Number,
            required: true,
        },
        currentDateDisplay: {
            type: String,
            required: true,
        },
        showDatePicker: {
            type: Boolean,
            default: false,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        canExport: {
            type: Boolean,
            default: false,
        },
        isExporting: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            localSelectedContractIdx: null,
        };
    },
    watch: {
        selectedContractIdx: {
            immediate: true,
            handler(newVal) {
                this.localSelectedContractIdx = newVal;
            },
        },
    },
    methods: {
        getStatusName,
        getContractDisplayText,
        handleContractSelect() {
            this.$emit("contract-select", this.localSelectedContractIdx);
        },
        handleToggleDatePicker() {
            this.$emit("date-picker-toggle");
        },
        handleCloseDatePicker() {
            this.$emit("date-picker-close");
        },
    },
};
</script>

<style scoped>
/* 헤더 — 왼쪽 제목은 타이포, 오른쪽만 알약 툴바.
   전폭 글래스를 걷어도 기간 드롭다운이 카드 위에 오도록 z-index는 유지한다. */
.dashboard-header {
    position: relative;
    z-index: 200;
    overflow: visible;
    background: transparent;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 2px 0;
    margin-bottom: 6px;
    flex-shrink: 0;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex: 1 1 auto;
}

.dashboard-title {
    font-size: 20px;
    font-weight: 800;
    color: var(--ebc-primary);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    letter-spacing: -0.2px;
    white-space: nowrap;
    flex-shrink: 0;
}

.dashboard-title i {
    font-size: 18px;
}

.status-message-container {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border-radius: var(--ebc-radius-pill);
    background: var(--ebc-track);
    min-width: 0;
    transition: all 0.2s var(--ebc-ease);
}

.status-message-container:hover {
    background: #e8edf3;
}

.status-message-container .status-marker {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
}

.status-message-container.pending .status-marker {
    background: var(--ebc-warn);
}

.status-message-container.predict .status-marker {
    background: var(--ebc-primary-soft);
}

.status-message-container.active .status-marker {
    background: var(--ebc-positive);
}

.status-message-container .status-label {
    font-size: 11px;
    font-weight: 700;
    color: var(--ebc-text);
    letter-spacing: 0.2px;
    white-space: nowrap;
}

.status-message-container .status-separator {
    font-size: 11px;
    color: var(--ebc-text-muted);
    margin: 0 2px;
}

/* 헤더가 꽉 차면 안내 문구부터 줄어들게 한다 (제목·컨트롤 줄바꿈 방지) */
.status-message-container .status-message {
    font-size: 11px;
    font-weight: 500;
    color: var(--ebc-text-sub);
    line-height: 1.4;
    letter-spacing: 0.2px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    padding: 3px 6px 3px 8px;
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline);
    border-radius: var(--ebc-radius-pill);
    box-shadow: var(--ebc-shadow-card);
}

.toolbar-divider {
    width: 1px;
    height: 16px;
    background: var(--ebc-hairline-strong);
    margin: 0 4px;
    flex-shrink: 0;
}

/* 분석 유형 — 세그먼트 컨트롤 */
.analysis-type-toggle {
    display: flex;
    align-items: center;
    gap: 2px;
    background: var(--ebc-track);
    border-radius: var(--ebc-radius-pill);
    padding: 3px;
}

.toggle-btn {
    padding: 5px 14px;
    background: transparent;
    color: var(--ebc-text-sub);
    border: none;
    border-radius: var(--ebc-radius-pill);
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    transition: all 0.2s var(--ebc-ease);
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
}

.toggle-btn:focus {
    outline: none;
}

.toggle-btn.active {
    background: var(--ebc-surface);
    color: var(--ebc-primary);
    box-shadow: var(--ebc-shadow-toggle);
}

.toggle-btn:hover:not(.active) {
    color: var(--ebc-text);
}

.date-picker-container {
    position: relative;
    display: flex;
    align-items: center;
    background: transparent;
    border-radius: var(--ebc-radius-pill);
    padding: 0;
    z-index: 1;
}

.nav-btn {
    background: transparent;
    border: none;
    padding: 5px 10px;
    cursor: pointer;
    color: var(--ebc-text-sub);
    transition: all 0.2s var(--ebc-ease);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--ebc-radius-pill);
}

.nav-btn:hover {
    color: var(--ebc-primary);
    background: var(--ebc-surface);
}

.nav-btn:focus {
    outline: none;
}

.nav-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.nav-btn:disabled:hover {
    background: transparent;
    color: var(--ebc-text-muted);
}

.date-display {
    padding: 5px 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    justify-content: center;
    border-radius: var(--ebc-radius-pill);
    transition: all 0.2s var(--ebc-ease);
    color: var(--ebc-text);
    font-weight: 600;
    font-size: 13px;
}

.date-display:hover {
    background: var(--ebc-surface);
    box-shadow: var(--ebc-shadow-toggle);
}

.date-display i {
    font-size: 10px;
    color: var(--ebc-text-sub);
    transition: transform 0.2s ease;
}

.date-display i.rotate {
    transform: rotate(180deg);
}

.loading-icon {
    font-size: 10px;
    color: var(--ebc-primary);
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.date-picker-container.loading {
    opacity: 0.7;
    pointer-events: none;
}

.date-picker-container.loading .date-display {
    cursor: wait;
}

.date-picker-container.loading .date-display span {
    opacity: 0.8;
}

.date-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline);
    border-radius: var(--ebc-radius-lg);
    box-shadow: var(--ebc-shadow-raised);
    z-index: 2;
    margin-top: 8px;
    padding: 16px;
    min-width: 240px;
}

.year-selector {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--ebc-hairline-strong);
}

.year-selector button {
    background: var(--ebc-surface-sunken);
    border: none;
    padding: 4px 10px;
    cursor: pointer;
    color: var(--ebc-text-sub);
    border-radius: var(--ebc-radius-sm);
    transition: all 0.2s var(--ebc-ease);
    font-weight: 600;
    font-size: 12px;
}

.year-selector button:focus {
    outline: none;
}

.year-selector button:hover {
    background: var(--ebc-primary);
    color: #ffffff;
}

.year-selector span {
    font-size: 14px;
    font-weight: 700;
    color: var(--ebc-text);
}

.month-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
}

.month-btn {
    background: var(--ebc-surface-sunken);
    border: none;
    padding: 8px 4px;
    cursor: pointer;
    border-radius: var(--ebc-radius-sm);
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text-sub);
    transition: all 0.2s var(--ebc-ease);
    text-align: center;
}

.month-btn:focus {
    outline: none;
}

.month-btn:hover {
    background: var(--ebc-primary-tint);
    color: var(--ebc-primary);
}

.month-btn.active {
    background: var(--ebc-primary);
    color: #ffffff;
    box-shadow: 0 2px 8px var(--ebc-primary-tint-strong);
}

/* 요금계약 — 툴바 안 텍스트+셰브론 */
.kepco-customer-select-container {
    position: relative;
    display: flex;
    align-items: center;
    max-width: 168px;
}

.kepco-customer-select {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    padding: 5px 20px 5px 8px;
    background: transparent;
    color: var(--ebc-text);
    border: none;
    border-radius: var(--ebc-radius-pill);
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    width: auto;
    min-width: 64px;
    max-width: 168px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: all 0.2s var(--ebc-ease);
}

.kepco-customer-select::-ms-expand {
    display: none;
}

.kepco-customer-select:focus {
    outline: none;
    background: var(--ebc-track);
}

.kepco-customer-select-container .select-chevron {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: 9px;
    color: var(--ebc-text-sub);
}

.kepco-customer-select option {
    background: var(--ebc-surface);
    color: var(--ebc-text);
}

/* 헤더 액션 — 툴바 안 고스트 */
.btn-refresh,
.btn-settings,
.kepco-pdf-btn,
.kepco-hourly-analysis-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 5px 10px;
    background: transparent;
    color: var(--ebc-text-sub);
    border: none;
    border-radius: var(--ebc-radius-pill);
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    transition: all 0.2s var(--ebc-ease);
    white-space: nowrap;
}

.btn-refresh,
.btn-settings {
    width: 28px;
    height: 28px;
    padding: 0;
    justify-content: center;
}

.btn-refresh:hover,
.btn-settings:hover,
.kepco-pdf-btn:hover,
.kepco-hourly-analysis-btn:hover {
    background: var(--ebc-primary-tint);
    color: var(--ebc-primary);
}

.btn-refresh:focus,
.btn-settings:focus,
.kepco-pdf-btn:focus,
.kepco-hourly-analysis-btn:focus {
    outline: none;
}

.btn-refresh:active,
.btn-settings:active,
.kepco-pdf-btn:active,
.kepco-hourly-analysis-btn:active {
    background: var(--ebc-primary-tint-strong);
}

.btn-refresh i,
.btn-settings i,
.kepco-pdf-btn i,
.kepco-hourly-analysis-btn i {
    font-size: 12px;
}

.kepco-pdf-btn:disabled,
.kepco-hourly-analysis-btn:disabled,
.kepco-hourly-analysis-btn.disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.kepco-pdf-btn:disabled:hover,
.kepco-hourly-analysis-btn:disabled:hover,
.kepco-hourly-analysis-btn.disabled:hover {
    background: transparent;
    color: var(--ebc-text-sub);
    box-shadow: none;
    transform: none;
}
</style>
